import { useAsyncState } from '@vueuse/core';
import { computed, ref } from 'vue';
import { RecaptchaAction } from '@getaccept/lib-shared/src/recaptcha/recaptcha-action';
import { AnalyticsUserTrait } from '@getaccept/lib-shared/src/types/analytics';
import { storeToRefs } from 'pinia';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import * as SignupHelper from '../helpers/signup.helper';
import SignupService from '../services/signup.service';
import type { ErrorMessage } from '../../common/types/error-message';
import type { SignupPayload } from '../types/signup-payload';
import { RecaptchaService } from '../services/recaptcha.service';
import { useGoogleTagManager } from '../../common/composables/google-tag-manager.composable';
import type { SignupResponse } from '../types/signup-response';
import { useLanguageStore } from '../../language/store/language.store';

export const useSignup = () => {
  const {
    getErrorMessage,
    setAnalyticsSignupError,
    setAnalyticsSignupSuccess,
    getNamesAndEntityFromEmail,
    getRedirectUrl,
  } = SignupHelper;
  const { language } = storeToRefs(useLanguageStore());

  const error = ref<ErrorMessage | undefined>();
  const email = ref<string>('');

  const { execute: getToken } = useAsyncState(
    RecaptchaService.getToken(RecaptchaAction.Signup),
    null,
    {
      immediate: false,
      onError: () => {
        error.value = getErrorMessage();
      },
    }
  );

  const setAnalyticsSignup = (countryName: string, email: string) => {
    const analyticsData = {
      [AnalyticsUserTrait.SignupMethod]: 'email',
      [AnalyticsUserTrait.SignupStep]: 1,
      [AnalyticsUserTrait.Email]: email,
      [AnalyticsUserTrait.Country]: countryName,
    };
    SignupHelper.setAnalyticsSignup(analyticsData);
  };

  const signup = async ({
    countryName,
    ...data
  }: Omit<SignupPayload, 'recaptchaToken'> & { countryName: string }) => {
    try {
      email.value = data.email;
      error.value = undefined;
      setAnalyticsSignup(countryName, data.email);

      const recaptchaToken = await getToken(0);
      if (!recaptchaToken) return;

      await useGoogleTagManager().initiateSignup({ email: data.email });

      return await SignupService.signup({ ...data, recaptchaToken });
    } catch (e) {
      setAnalyticsSignupError(error.value);
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
    }
  };

  const setErrorAnalytics = () => {
    setAnalyticsSignupError(error.value);
    useGoogleTagManager().initiateSignupError({ email: email.value, error: error.value?.title });
  };

  const checkError = (response: SignupResponse) => {
    if (!response || (response?.status === 1 && !response?.error)) return;
    bugsnagClient?.notify(new Error(JSON.stringify(response)));
    if (response?.status !== 2) {
      error.value = getErrorMessage(response);
    }
    setErrorAnalytics();
  };

  const redirectToSuccessPage = (registrationUrl?: string) => {
    const { firstName, lastName, entityName } = getNamesAndEntityFromEmail(email.value);
    const queryParams = {
      firstName,
      lastName,
      lang: language.value,
      entityName,
      entityCountryCode: '',
    };

    const url = getRedirectUrl({ registrationUrl, queryParams });
    window.location.href = url;
  };

  const onSuccess = async (response: SignupResponse) => {
    checkError(response);
    if (error.value) return;
    await setAnalyticsSignupSuccess(email.value, response.registration_url, response.email_score);
    redirectToSuccessPage(response.registration_url);
  };

  const { state: response, ...query } = useAsyncState(signup, undefined, {
    immediate: false,
    onError: (e: SignupResponse) => {
      error.value = getErrorMessage(e);
      setErrorAnalytics();
    },
    onSuccess,
  });

  const didYouMean = computed(
    () => error.value?.variables?.didYouMean || response.value?.did_you_mean || ''
  );

  return { ...query, error, didYouMean };
};
